import { useSearchParams } from 'next/navigation';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { UsePagingFragmentFragmentDoc } from '@packages/gql/generated/shopping/UsePagingFragmentFragmentDoc';
import { searchParamsToObject, usePushQueryChange } from '@packages/shared';

import {
  decodePage,
  decodePageSize,
  decodePagingMode,
  encodePage,
  encodePagingMode,
} from '../queryEncoding';
import { useDefaultPageSize } from './useDefaultPageSize';
import { useFeatureFlag } from '../utils/featureFlags/useFeatureFlag';
import { newInfiniteScrolling } from '../activeFeatureFlags';

/* GraphQL */ `
  fragment UsePagingFragment on SearchProductResult {
    summary {
      totalResultCount
    }
  }
`;

const DEFAULT_PAGE = 1;

/**
 * Use page option persisted in the url query
 *
 * Changing pages results in a router.push, which will trigger a data reload
 */
export const usePaging = (maskedData: FragmentType<typeof UsePagingFragmentFragmentDoc>) => {
  const data = getFragmentData(UsePagingFragmentFragmentDoc, maskedData);

  const searchParams = useSearchParams();
  const { pushQueryChange } = usePushQueryChange({ trailingSlash: true });

  const defaultPageSize = useDefaultPageSize();
  const hasNewInfiniteScrolling = useFeatureFlag(newInfiniteScrolling);

  const query = searchParamsToObject(searchParams);
  const pageSize = decodePageSize(query) ?? defaultPageSize;
  const currentPage = decodePage(query) ?? DEFAULT_PAGE;
  const pagingModeFromQuery = decodePagingMode(query);

  const pagingMode = hasNewInfiniteScrolling ? 'infinite' : pagingModeFromQuery;
  const pageCount = Math.ceil(data.summary.totalResultCount / pageSize);

  const pushPageChange = (newPage: number, keepScrollPosition?: boolean) => {
    if (newPage === currentPage) return;

    const partialQuery = encodePage(newPage === DEFAULT_PAGE ? undefined : newPage);

    pushQueryChange(partialQuery, {
      scroll: !keepScrollPosition,
    });
  };

  const pushPagingMode = (newPagingMode: 'pagination' | 'infinite') => {
    // new infinite scrolling only supports infinite mode, no pagination
    if (hasNewInfiniteScrolling) return;
    // if mode is already correct, do nothing
    if (newPagingMode === pagingMode) return;

    const partialQuery = {
      // reset page to the default when switching modes
      ...encodePage(undefined),
      ...encodePagingMode(newPagingMode),
    };

    pushQueryChange(partialQuery);
  };

  return {
    pageSize,
    pageCount,
    currentPage,
    pagingMode,
    pushPageChange,
    pushPagingMode,
  };
};
